// Default style with dark mode support
export const defaultStyle = {
    option: (provided: any, state: any) => ({
        ...provided,
        color: "#2A2A2A",
        backgroundColor: state.isSelected ? "#F3F3F3" : "#FFFFFF",
        ":hover": {
            backgroundColor: state.isSelected ? "#F3F3F3" : "#DDE8F8",
            color: "#2A2A2A",
        },
        cursor: "pointer",
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: "#FFFFFF",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        zIndex: 10,
    }),
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: "#FFFFFF",
        borderColor: "#cccccc",
        boxShadow: state.isFocused ? "0 0 0 1px #007a0b" : "none",
        "&:hover": {
            borderColor: "#007a0b",
        },
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: "#2A2A2A",
    }),
    input: (provided: any) => ({
        ...provided,
        color: "#2A2A2A",
    }),
    valueContainer: (provided: any) => ({
        ...provided,
        padding: "2px 8px",
    }),
    indicatorSeparator: (provided: any) => ({
        ...provided,
        backgroundColor: "#cccccc",
    }),
    dropdownIndicator: (provided: any) => ({
        ...provided,
        color: "#666666",
        "&:hover": {
            color: "#007a0b",
        },
    }),
};

// Get the default style with dark mode variations
export const getDefaultStyleWithDarkMode = (isDarkMode: boolean = false) => {
    return {
        option: (provided: any, state: any) => ({
            ...provided,
            color: isDarkMode ? "#FFFFFF" : "#2A2A2A",
            backgroundColor: isDarkMode
                ? (state.isSelected ? "#444444" : "#222222")
                : (state.isSelected ? "#F3F3F3" : "#FFFFFF"),
            ":hover": {
                backgroundColor: isDarkMode
                    ? (state.isSelected ? "#444444" : "#333333")
                    : (state.isSelected ? "#F3F3F3" : "#DDE8F8"),
                color: isDarkMode ? "#FFFFFF" : "#2A2A2A",
            },
            cursor: "pointer",
        }),
        menu: (provided: any) => ({
            ...provided,
            backgroundColor: isDarkMode ? "#222222" : "#FFFFFF",
            boxShadow: isDarkMode ? "0px 4px 12px rgba(0, 0, 0, 0.3)" : "0px 4px 12px rgba(0, 0, 0, 0.15)",
            zIndex: 10,
        }),
        control: (provided: any, state: any) => ({
            ...provided,
            backgroundColor: isDarkMode ? "#444444" : "#FFFFFF",
            borderColor: isDarkMode ? "#666666" : "#cccccc",
            boxShadow: state.isFocused
                ? (isDarkMode ? "0 0 0 1px #00520c" : "0 0 0 1px #007a0b")
                : "none",
            "&:hover": {
                borderColor: isDarkMode ? "#00520c" : "#007a0b",
            },
            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: isDarkMode ? "#FFFFFF" : "#2A2A2A",
        }),
        input: (provided: any) => ({
            ...provided,
            color: isDarkMode ? "#FFFFFF" : "#2A2A2A",
        }),
        valueContainer: (provided: any) => ({
            ...provided,
            padding: "2px 8px",
        }),
        indicatorSeparator: (provided: any) => ({
            ...provided,
            backgroundColor: isDarkMode ? "#666666" : "#cccccc",
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: isDarkMode ? "#cccccc" : "#666666",
            "&:hover": {
                color: isDarkMode ? "#00520c" : "#007a0b",
            },
        }),
    };
};

export const languageSelectStyle = {
    option: (provided: any, state: any) => ({
        ...provided,
        color: "#2A2A2A",
        backgroundColor: state.isSelected ? "#F3F3F3" : "#FFFFFF",
        ":hover": {
            backgroundColor: state.isSelected ? "#F3F3F3" : "#DDE8F8",
            color: "#2A2A2A",
        },
        cursor: "pointer",
        "@media (prefers-color-scheme: dark)": {
            color: "#FFFFFF",
            backgroundColor: state.isSelected ? "#444444" : "#222222",
            ":hover": {
                backgroundColor: state.isSelected ? "#444444" : "#333333",
                color: "#FFFFFF",
            },
        }
    }),
    input: (provided: any) => ({
        ...provided,
        color: "#FFFFFF", // Set text color to white to contrast with dark background
        "@media (prefers-color-scheme: dark)": {
            color: "#FFFFFF", // Set text color to white in dark mode
        }
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: "#FFFFFF", // white for light mode
        width: "fit-content",
        minWidth: "auto",
        "@media (prefers-color-scheme: dark)": {
            backgroundColor: "#181818", // off-black for dark mode
        }
    }),
    control: (provided: any, state: any) => ({
        ...provided,
        padding: "0px 0px",
        color: "#2A2A2A",
        backgroundColor: "#FFFFFF", // white for light mode
        border: "none", // No border in light mode
        boxShadow: state.menuIsOpen ? "0 0 0 1px #007a0b" : "none",
        ":hover": {
            borderColor: state.menuIsOpen ? "#007a0b" : provided.borderColor,
        },
        width: "fit-content",
        height: "fit-content",
        cursor: "pointer",
        "@media (prefers-color-scheme: dark)": {
            backgroundColor: "#181818",
            color: "#FFFFFF",
            border: "none",
            boxShadow: state.menuIsOpen ? "0 0 0 1px #FFFFFF" : "none",
        }
    }),
    valueContainer: (provided: any) => ({
        ...provided,
        padding: "0px 0px 0px 8px ", // Adjust the padding to decrease space
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        color: "#666666",
        padding: "0px 8px 0px 1px",
        visibility: state.isDisabled ? "hidden" : "visible",
        ":hover": {
            color: "#007a0b",
        },
        "@media (prefers-color-scheme: dark)": {
            color: "#FFFFFF",
            ":hover": {
                color: "#DDE8F8",
            },
        }
    }),
    indicatorsContainer: (provided: any) => ({
        ...provided,
        padding: "0px",
        margin: "0px",
    }),
    indicatorSeparator: (provided: any, state: any) => ({
        ...provided,
        visibility: "hidden",
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: "#6B7280",
        "@media (prefers-color-scheme: dark)": {
            color: "#F5F5F5",
        }
    }),
};

export const timezoneStyle = {
    option: (provided: any, state: any) => ({
        ...provided,
        color: "#2A2A2A",
        backgroundColor: state.isSelected ? "#F3F3F3" : "#FFFFFF", // white for light mode
        ":hover": {
            backgroundColor: state.isSelected ? "#F3F3F3" : "#DDE8F8",
            color: "#2A2A2A",
        },
        cursor: "pointer",
        "@media (prefers-color-scheme: dark)": {
            color: "#FFFFFF",
            backgroundColor: state.isSelected ? "#444444" : "#222222",
            ":hover": {
                backgroundColor: state.isSelected ? "#444444" : "#333333",
                color: "#FFFFFF",
            },
        }
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: "#FFFFFF", // white for light mode
        width: "auto",
        right: 0,
        "@media (prefers-color-scheme: dark)": {
            backgroundColor: "#222222",
        }
    }),
    control: (provided: any, state: any) => ({
        ...provided,
        padding: "0px 0px",
        color: "#2A2A2A",
        backgroundColor: "#FFFFFF", // white for light mode
        border: "none", // No border in light mode
        boxShadow: state.menuIsOpen ? "0 0 0 1px #007a0b" : "none",
        ":hover": {
            borderColor: state.menuIsOpen ? "#007a0b" : provided.borderColor,
        },
        width: "fit-content",
        height: "fit-content",
        cursor: "pointer",
        "@media (prefers-color-scheme: dark)": {
            backgroundColor: "#222222",
            color: "#FFFFFF",
            border: "none",
        }
    }),
    input: (provided: any) => ({
        ...provided,
        color: "#FFFFFF", // Set text color to white to contrast with dark background
        "@media (prefers-color-scheme: dark)": {
            color: "#FFFFFF",
        }
    }),
    valueContainer: (provided: any) => ({
        ...provided,
        padding: "0px 0px 0px 8px ", // Adjust the padding to decrease space
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        color: "#000000", // Black for light mode
        padding: "0px 8px 0px 1px",
        visibility: state.isDisabled ? "hidden" : "visible",
        ":hover": {
            color: "#000000", // Black for light mode
        },
        "@media (prefers-color-scheme: dark)": {
            color: "#FFFFFF",
            ":hover": {
                color: "#FFFFFF",
            },
        }
    }),
    indicatorsContainer: (provided: any) => ({
        ...provided,
        padding: "0px",
        margin: "0px",
    }),
    indicatorSeparator: (provided: any, state: any) => ({
        ...provided,
        visibility: "hidden",
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: "#000000", // Black for light mode
        "@media (prefers-color-scheme: dark)": {
            color: "#cccccc",
        }
    }),
}

export const activityLogStyle = {
    option: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isSelected ? "#e6f5e6" : "#FFFFFF",
        color: state.isSelected ? "#4a4a4a" : "#2A2A2A",
        cursor: "pointer",
        ":hover": {
            backgroundColor: state.isSelected ? "#e6f5e6" : "#f0f0f0",
        },
        "@media (prefers-color-scheme: dark)": {
            backgroundColor: state.isSelected ? "#043009" : "#222222",
            color: state.isSelected ? "#FFFFFF" : "#FFFFFF",
            ":hover": {
                backgroundColor: state.isSelected ? "#043009" : "#333333",
            },
        }
    }),
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: "#FFFFFF", // Explicitly set to white in light mode
        // Add green border when there's a selected value
        borderColor: state.hasValue ? "#007a0b" : "#cccccc",
        boxShadow: state.isFocused
            ? (state.hasValue ? "0 0 0 0.25px #007a0b" : "0 0 0 0.25px #cccccc")
            : "none",
        "&:hover": {
            borderColor: state.hasValue ? "#45a049" : "#bbbbbb", // Darker green on hover
        },
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        "@media (prefers-color-scheme: dark)": {
            backgroundColor: "#444444",
            borderColor: state.hasValue ? "#00520c" : "#666666",
            boxShadow: state.isFocused
                ? (state.hasValue ? "0 0 0 0.25px #00520c" : "0 0 0 0.25px #666666")
                : "none",
            "&:hover": {
                borderColor: state.hasValue ? "#00520c" : "#888888",
            },
        }
    }),
    valueContainer: (provided: any) => ({
        ...provided,
        padding: "4px 8px",
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: "#4a4a4a", // Dark medium gray text
        backgroundColor: "transparent", // No background on the container
        padding: 0,
        "& span": { // Style the inner text span
            backgroundColor: "#E8F2E9", // Light green background
            padding: "2px 6px",
            borderRadius: "3px",
        },
        "@media (prefers-color-scheme: dark)": {
            color: "#FFFFFF",
            "& span": {
                backgroundColor: "#043009",
            }
        }
    }),
    dropdownIndicator: (provided: any) => ({
        ...provided,
        color: '#666666',
        '&:hover': {
            color: '#007a0b',
        },
        padding: '8px',
        cursor: 'pointer',
        "@media (prefers-color-scheme: dark)": {
            color: '#cccccc',
            '&:hover': {
                color: '#00520c',
            },
        }
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: "#FFFFFF", // Explicitly set to white in light mode
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        "@media (prefers-color-scheme: dark)": {
            backgroundColor: "#222222",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
        }
    }),
    input: (provided: any) => ({
        ...provided,
        color: "#2A2A2A",
        "@media (prefers-color-scheme: dark)": {
            color: "#FFFFFF",
        }
    }),
    indicatorSeparator: (provided: any) => ({
        ...provided,
        backgroundColor: "#cccccc",
        "@media (prefers-color-scheme: dark)": {
            backgroundColor: "#666666",
        }
    }),
}